/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */
import { addToCartDelegator } from '@/data/events/delegators/AddToCart';
import { bannerClickDelegator } from '@/data/events/delegators/BannerClick';
import { bannerViewDelegator } from '@/data/events/delegators/BannerView';
import { cartPageViewDelegator } from '@/data/events/delegators/CartPageView';
import { cartViewDelegator } from '@/data/events/delegators/CartView';
import { checkoutDelegator } from '@/data/events/delegators/Checkout';
import { checkoutPageViewDelegator } from '@/data/events/delegators/CheckoutPageView';
import { checkoutPaymentDelegator } from '@/data/events/delegators/CheckoutPayment';
import { checkoutShippingDelegator } from '@/data/events/delegators/CheckoutShipping';
import { cookieDialogAcceptDelegator } from '@/data/events/delegators/CookieDialogAccept';
import { itemListViewDelegator } from '@/data/events/delegators/ItemListView';
import { carouselItemListViewDelegator } from '@/data/events/delegators/CarouselItemListView';
import { carouselProductClickDelegator } from '@/data/events/delegators/CarouselProductClick';
import { productClickDelegator } from '@/data/events/delegators/ProductClick';
import { productViewDelegator } from '@/data/events/delegators/ProductView';
import { promotionClickDelegator } from '@/data/events/delegators/PromotionClick';
import { promotionViewDelegator } from '@/data/events/delegators/PromotionView';
import { purchaseDelegator } from '@/data/events/delegators/Purchase';
import { removeFromCartDelegator } from '@/data/events/delegators/RemoveFromCart';
import { searchResultsViewDelegator } from '@/data/events/delegators/SearchResultsView';
import { signInDelegator } from '@/data/events/delegators/SignIn';
import { signUpDelegator } from '@/data/events/delegators/SignUp';
import { loginLessBeginDelegator } from '@/data/events/delegators/LoginLessBegin';
import { loginLessMethodDelegator } from '@/data/events/delegators/LoginLessMethod';
import { loginLessSubmitDelegator } from '@/data/events/delegators/LoginLessSubmit';
import { loginOpenDelegator } from '@/data/events/delegators/LoginOpen';
import { registerBeginDelegator } from '@/data/events/delegators/RegisterBegin';
import { passwordRecoveryBeginDelegator } from '@/data/events/delegators/PasswordRecoveryBegin';
import { passwordRecoveryCompleteDelegator } from '@/data/events/delegators/PasswordRecoveryComplete';
import { viewLookDelegator } from '@/data/events/delegators/ViewLook';
import { multiAddToCartDelegator } from '@/data/events/delegators/MultiAddToCart';
import { PageDataFromId } from '@/data/types/PageDataFromId';
import { ProductType } from '@/data/types/Product';
import { noop } from 'lodash';
import { FC, PropsWithChildren, createContext, useCallback, useState } from 'react';

type EventHandlers = {
	onPageView: (page: PageDataFromId['page']) => void;
	onProductView: typeof productViewDelegator;
	onBannerView: typeof bannerViewDelegator;
	onBannerClick: typeof bannerClickDelegator;
	onCookieDialogAccept: typeof cookieDialogAcceptDelegator;
	onSignIn: typeof signInDelegator;
	onSignUp: typeof signUpDelegator;
	onProductClick: typeof productClickDelegator;
	onCartPageView: typeof cartPageViewDelegator;
	onCartView: typeof cartViewDelegator;
	onAddToCart: typeof addToCartDelegator;
	onAddToWishlist: (product: ProductType, quantity: number) => void;
	onCheckout: typeof checkoutDelegator;
	onCheckoutShipping: typeof checkoutShippingDelegator;
	onCheckoutPayment: typeof checkoutPaymentDelegator;
	onCheckoutPageView: typeof checkoutPageViewDelegator;
	onEmptyCart: () => void;
	onPromotionClick: typeof promotionClickDelegator;
	onPromotionView: typeof promotionViewDelegator;
	onSearchResultsView: typeof searchResultsViewDelegator;
	onItemListView: typeof itemListViewDelegator;
	onCarouselItemListView: typeof carouselItemListViewDelegator;
	onCarouselProductClick: typeof carouselProductClickDelegator;
	onRemoveFromCart: typeof removeFromCartDelegator;
	onPurchase: typeof purchaseDelegator;
	onLoginLessBegin: typeof loginLessBeginDelegator;
	onLoginLessMethod: typeof loginLessMethodDelegator;
	onLoginLessSubmit: typeof loginLessSubmitDelegator;
	onLoginOpen: typeof loginOpenDelegator;
	onRegisterBegin: typeof registerBeginDelegator;
	onPasswordRecoveryBegin: typeof passwordRecoveryBeginDelegator;
	onPasswordRecoveryComplete: typeof passwordRecoveryCompleteDelegator;
	onViewLook: typeof viewLookDelegator;
	onMultiAddToCart: typeof multiAddToCartDelegator;
};

type RegisterEvent = <T extends keyof EventHandlers>(
	eventType: T,
	handler: EventHandlers[T]
) => void;

type EventData = Record<keyof EventHandlers, any>;

export type EventsContextType = {
	addEventData: (eventType: keyof EventHandlers, data: any) => void;
	clearEventData: (eventType: keyof EventHandlers) => void;
	eventData: EventData;
	registerEvent: RegisterEvent;
	onPageView: EventHandlers['onPageView'];
	onProductView: EventHandlers['onProductView'];
	onBannerView: EventHandlers['onBannerView'];
	onBannerClick: EventHandlers['onBannerClick'];
	onCookieDialogAccept: EventHandlers['onCookieDialogAccept'];
	onSignIn: EventHandlers['onSignIn'];
	onSignUp: EventHandlers['onSignUp'];
	onProductClick: EventHandlers['onProductClick'];
	onCartPageView: EventHandlers['onCartPageView'];
	onCartView: EventHandlers['onCartView'];
	onAddToCart: EventHandlers['onAddToCart'];
	onAddToWishlist: EventHandlers['onAddToWishlist'];
	onCheckout: EventHandlers['onCheckout'];
	onCheckoutShipping: EventHandlers['onCheckoutShipping'];
	onCheckoutPayment: EventHandlers['onCheckoutPayment'];
	onCheckoutPageView: EventHandlers['onCheckoutPageView'];
	onEmptyCart: EventHandlers['onEmptyCart'];
	onPromotionClick: EventHandlers['onPromotionClick'];
	onPromotionView: EventHandlers['onPromotionView'];
	onSearchResultsView: EventHandlers['onSearchResultsView'];
	onItemListView: EventHandlers['onItemListView'];
	onCarouselItemListView: EventHandlers['onCarouselItemListView'];
	onCarouselProductClick: EventHandlers['onCarouselProductClick'];
	onRemoveFromCart: EventHandlers['onRemoveFromCart'];
	onPurchase: EventHandlers['onPurchase'];
	onLoginLessBegin: EventHandlers['onLoginLessBegin'];
	onLoginLessMethod: EventHandlers['onLoginLessMethod'];
	onLoginLessSubmit: EventHandlers['onLoginLessSubmit'];
	onLoginOpen: EventHandlers['onLoginOpen'];
	onRegisterBegin: EventHandlers['onRegisterBegin'];
	onPasswordRecoveryBegin: EventHandlers['onPasswordRecoveryBegin'];
	onPasswordRecoveryComplete: EventHandlers['onPasswordRecoveryComplete'];
	onViewLook: EventHandlers['onViewLook'];
	onMultiAddToCart: EventHandlers['onMultiAddToCart'];
};

export const EventsContext = createContext<EventsContextType>({
	addEventData: noop,
	clearEventData: noop,
	eventData: {} as EventData,
	registerEvent: noop,
	onPageView: noop,
	onProductView: noop as EventHandlers['onProductView'],
	onBannerClick: noop as EventHandlers['onBannerClick'],
	onCookieDialogAccept: noop as EventHandlers['onCookieDialogAccept'],
	onSignIn: noop as EventHandlers['onSignIn'],
	onSignUp: noop as EventHandlers['onSignUp'],
	onBannerView: noop as EventHandlers['onBannerView'],
	onProductClick: noop as EventHandlers['onProductClick'],
	onCartPageView: noop as EventHandlers['onCartPageView'],
	onCartView: noop as EventHandlers['onCartView'],
	onAddToCart: noop as EventHandlers['onAddToCart'],
	onAddToWishlist: noop,
	onCheckout: noop as EventHandlers['onCheckout'],
	onCheckoutPayment: noop as EventHandlers['onCheckoutPayment'],
	onCheckoutShipping: noop as EventHandlers['onCheckoutShipping'],
	onCheckoutPageView: noop as EventHandlers['onCheckoutPageView'],
	onEmptyCart: noop,
	onPromotionClick: noop as EventHandlers['onPromotionClick'],
	onPromotionView: noop as EventHandlers['onPromotionView'],
	onSearchResultsView: noop as EventHandlers['onSearchResultsView'],
	onItemListView: noop as EventHandlers['onItemListView'],
	onCarouselItemListView: noop as EventHandlers['onCarouselItemListView'],
	onCarouselProductClick: noop as EventHandlers['onCarouselProductClick'],
	onRemoveFromCart: noop as EventHandlers['onRemoveFromCart'],
	onPurchase: noop as EventHandlers['onPurchase'],
	onLoginLessBegin: noop as EventHandlers['onLoginLessBegin'],
	onLoginLessMethod: noop as EventHandlers['onLoginLessMethod'],
	onLoginLessSubmit: noop as EventHandlers['onLoginLessSubmit'],
	onLoginOpen: noop as EventHandlers['onLoginOpen'],
	onRegisterBegin: noop as EventHandlers['onRegisterBegin'],
	onPasswordRecoveryBegin: noop as EventHandlers['onPasswordRecoveryBegin'],
	onPasswordRecoveryComplete: noop as EventHandlers['onPasswordRecoveryComplete'],
	onViewLook: noop as EventHandlers['onViewLook'],
	onMultiAddToCart: noop as EventHandlers['onMultiAddToCart'],
});
const Provider = EventsContext.Provider;

export const EventsProvider: FC<PropsWithChildren> = ({ children }) => {
	const [onPageView, setOnPageView] = useState<EventHandlers['onPageView']>(() => noop);
	const [onProductView, setOnProductView] = useState<EventHandlers['onProductView']>(
		() => noop as EventHandlers['onProductView']
	);
	const [onBannerView, setOnBannerView] = useState<EventHandlers['onBannerView']>(
		() => noop as EventHandlers['onBannerView']
	);
	const [onBannerClick, setOnBannerClick] = useState<EventHandlers['onBannerClick']>(
		() => noop as EventHandlers['onBannerClick']
	);
	const [onCookieDialogAccept, setOnCookieDialogAccept] = useState<
		EventHandlers['onCookieDialogAccept']
	>(() => noop as EventHandlers['onCookieDialogAccept']);
	const [onSignIn, setOnSignIn] = useState<EventHandlers['onSignIn']>(
		() => noop as EventHandlers['onSignIn']
	);
	const [onSignUp, setOnSignUp] = useState<EventHandlers['onSignUp']>(
		() => noop as EventHandlers['onSignUp']
	);
	const [onProductClick, setOnProductClick] = useState<EventHandlers['onProductClick']>(
		() => noop as EventHandlers['onProductClick']
	);
	const [onCartPageView, setOnCartPageView] = useState<EventHandlers['onCartPageView']>(
		() => noop as EventHandlers['onCartPageView']
	);
	const [onCartView, setOnCartView] = useState<EventHandlers['onCartView']>(
		() => noop as EventHandlers['onCartView']
	);
	const [onAddToCart, setOnAddToCart] = useState<EventHandlers['onAddToCart']>(
		() => noop as EventHandlers['onAddToCart']
	);
	const [onAddToWishlist, setOnAddToWishlist] = useState<EventHandlers['onAddToWishlist']>(
		() => noop
	);
	const [onCheckout, setOnCheckout] = useState<EventHandlers['onCheckout']>(
		() => noop as EventHandlers['onCheckout']
	);
	const [onCheckoutShipping, setOnCheckoutShipping] = useState<EventHandlers['onCheckoutShipping']>(
		() => noop as EventHandlers['onCheckoutShipping']
	);
	const [onCheckoutPayment, setOnCheckoutPayment] = useState<EventHandlers['onCheckoutPayment']>(
		() => noop as EventHandlers['onCheckoutPayment']
	);
	const [onCheckoutPageView, setOnCheckoutPageView] = useState<EventHandlers['onCheckoutPageView']>(
		() => noop as EventHandlers['onCheckoutPageView']
	);
	const [onRemoveFromCart, setOnRemoveFromCart] = useState<EventHandlers['onRemoveFromCart']>(
		() => noop as EventHandlers['onRemoveFromCart']
	);
	const [onEmptyCart, setOnEmptyCart] = useState<EventHandlers['onEmptyCart']>(
		() => noop as EventHandlers['onRemoveFromCart']
	);
	const [onPromotionClick, setOnPromotionClick] = useState<EventHandlers['onPromotionClick']>(
		() => noop as EventHandlers['onPromotionClick']
	);
	const [onPromotionView, setOnPromotionView] = useState<EventHandlers['onPromotionView']>(
		() => noop as EventHandlers['onPromotionView']
	);
	const [onSearchResultsView, setOnSearchResultsView] = useState<
		EventHandlers['onSearchResultsView']
	>(() => noop as EventHandlers['onSearchResultsView']);
	const [onItemListView, setOnItemListView] = useState<EventHandlers['onItemListView']>(
		() => noop as EventHandlers['onItemListView']
	);
	const [onCarouselItemListView, setOnCarouselItemListView] = useState<
		EventHandlers['onCarouselItemListView']
	>(() => noop as EventHandlers['onCarouselItemListView']);
	const [onCarouselProductClick, setOnCarouselProductClick] = useState<
		EventHandlers['onCarouselProductClick']
	>(() => noop as EventHandlers['onCarouselProductClick']);
	const [onPurchase, setOnPurchase] = useState<EventHandlers['onPurchase']>(
		() => noop as EventHandlers['onPurchase']
	);
	const [onLoginLessBegin, setOnLoginLessBegin] = useState<EventHandlers['onLoginLessBegin']>(
		() => noop as EventHandlers['onLoginLessBegin']
	);
	const [onLoginLessMethod, setOnLoginLessMethod] = useState<EventHandlers['onLoginLessMethod']>(
		() => noop as EventHandlers['onLoginLessMethod']
	);
	const [onLoginLessSubmit, setOnLoginLessSubmit] = useState<EventHandlers['onLoginLessSubmit']>(
		() => noop as EventHandlers['onLoginLessSubmit']
	);
	const [onLoginOpen, setOnLoginOpen] = useState<EventHandlers['onLoginOpen']>(
		() => noop as EventHandlers['onLoginOpen']
	);
	const [onRegisterBegin, setOnRegisterBegin] = useState<EventHandlers['onRegisterBegin']>(
		() => noop as EventHandlers['onRegisterBegin']
	);
	const [onPasswordRecoveryBegin, setOnPasswordRecoveryBegin] = useState<
		EventHandlers['onPasswordRecoveryBegin']
	>(() => noop as EventHandlers['onPasswordRecoveryBegin']);
	const [onPasswordRecoveryComplete, setOnPasswordRecoveryComplete] = useState<
		EventHandlers['onPasswordRecoveryComplete']
	>(() => noop as EventHandlers['onPasswordRecoveryComplete']);
	const [onViewLook, setOnViewLook] = useState<EventHandlers['onViewLook']>(
		() => noop as EventHandlers['onViewLook']
	);
	const [onMultiAddToCart, setOnMultiAddToCart] = useState<EventHandlers['onMultiAddToCart']>(
		() => noop as EventHandlers['onMultiAddToCart']
	);
	const [eventData, setEventData] = useState<EventData>({} as EventData);
	const addEventData = useCallback((eventType: keyof EventHandlers, eventData: any) => {
		setEventData((old) => ({
			...old,
			[eventType]: old[eventType] ? { ...old[eventType], ...eventData } : { ...eventData },
		}));
	}, []);
	const clearEventData = useCallback((eventType: keyof EventHandlers) => {
		setEventData((old) => ({ ...old, [eventType]: {} }));
	}, []);

	// eslint-disable-next-line complexity
	const registerEvent: RegisterEvent = useCallback((eventType, handler) => {
		switch (eventType) {
			case 'onPageView':
				setOnPageView(() => handler);
				break;
			case 'onProductView':
				setOnProductView(() => handler as EventHandlers['onProductView']);
				break;
			case 'onBannerView':
				setOnBannerView(() => handler as EventHandlers['onBannerView']);
				break;
			case 'onBannerClick':
				setOnBannerClick(() => handler as EventHandlers['onBannerClick']);
				break;
			case 'onCookieDialogAccept':
				setOnCookieDialogAccept(() => handler as EventHandlers['onCookieDialogAccept']);
				break;
			case 'onSignIn':
				setOnSignIn(() => handler as EventHandlers['onSignIn']);
				break;
			case 'onSignUp':
				setOnSignUp(() => handler as EventHandlers['onSignUp']);
				break;
			case 'onProductClick':
				setOnProductClick(() => handler as EventHandlers['onProductClick']);
				break;
			case 'onCartPageView':
				setOnCartPageView(() => handler as EventHandlers['onCartPageView']);
				break;
			case 'onCartView':
				setOnCartView(() => handler as EventHandlers['onCartView']);
				break;
			case 'onAddToCart':
				setOnAddToCart(() => handler as EventHandlers['onAddToCart']);
				break;
			case 'onAddToWishlist':
				setOnAddToWishlist(() => handler);
				break;
			case 'onCheckout':
				setOnCheckout(() => handler as EventHandlers['onCheckout']);
				break;
			case 'onCheckoutShipping':
				setOnCheckoutShipping(() => handler as EventHandlers['onCheckoutShipping']);
				break;
			case 'onCheckoutPayment':
				setOnCheckoutPayment(() => handler as EventHandlers['onCheckoutPayment']);
				break;
			case 'onCheckoutPageView':
				setOnCheckoutPageView(() => handler as EventHandlers['onCheckoutPageView']);
				break;
			case 'onPromotionClick':
				setOnPromotionClick(() => handler as EventHandlers['onPromotionClick']);
				break;
			case 'onItemListView':
				setOnItemListView(() => handler as EventHandlers['onItemListView']);
				break;
			case 'onCarouselItemListView':
				setOnCarouselItemListView(() => handler as EventHandlers['onCarouselItemListView']);
				break;
			case 'onCarouselProductClick':
				setOnCarouselProductClick(() => handler as EventHandlers['onCarouselProductClick']);
				break;
			case 'onSearchResultsView':
				setOnSearchResultsView(() => handler as EventHandlers['onSearchResultsView']);
				break;
			case 'onRemoveFromCart':
				setOnRemoveFromCart(() => handler as EventHandlers['onRemoveFromCart']);
				break;
			case 'onEmptyCart':
				setOnEmptyCart(() => handler as EventHandlers['onEmptyCart']);
				break;
			case 'onPurchase':
				setOnPurchase(() => handler as EventHandlers['onPurchase']);
				break;
			case 'onPromotionView':
				setOnPromotionView(() => handler as EventHandlers['onPromotionView']);
				break;
			case 'onLoginLessBegin':
				setOnLoginLessBegin(() => handler as EventHandlers['onLoginLessBegin']);
				break;
			case 'onLoginLessMethod':
				setOnLoginLessMethod(() => handler as EventHandlers['onLoginLessMethod']);
				break;
			case 'onLoginLessSubmit':
				setOnLoginLessSubmit(() => handler as EventHandlers['onLoginLessSubmit']);
				break;
			case 'onLoginOpen':
				setOnLoginOpen(() => handler as EventHandlers['onLoginOpen']);
				break;
			case 'onRegisterBegin':
				setOnRegisterBegin(() => handler as EventHandlers['onRegisterBegin']);
				break;
			case 'onPasswordRecoveryBegin':
				setOnPasswordRecoveryBegin(() => handler as EventHandlers['onPasswordRecoveryBegin']);
				break;
			case 'onPasswordRecoveryComplete':
				setOnPasswordRecoveryComplete(() => handler as EventHandlers['onPasswordRecoveryComplete']);
				break;
			case 'onViewLook':
				setOnViewLook(() => handler as EventHandlers['onViewLook']);
				break;
			case 'onMultiAddToCart':
				setOnMultiAddToCart(() => handler as EventHandlers['onMultiAddToCart']);
				break;
		}
	}, []);

	return (
		<Provider
			value={{
				registerEvent,
				addEventData,
				clearEventData,
				eventData,
				onPageView,
				onProductView,
				onBannerView,
				onBannerClick,
				onCookieDialogAccept,
				onSignIn,
				onSignUp,
				onProductClick,
				onCartPageView,
				onCartView,
				onAddToCart,
				onAddToWishlist,
				onCheckout,
				onCheckoutShipping,
				onCheckoutPayment,
				onCheckoutPageView,
				onEmptyCart,
				onPromotionClick,
				onPromotionView,
				onSearchResultsView,
				onItemListView,
				onCarouselItemListView,
				onCarouselProductClick,
				onRemoveFromCart,
				onPurchase,
				onLoginLessBegin,
				onLoginLessMethod,
				onLoginLessSubmit,
				onLoginOpen,
				onRegisterBegin,
				onPasswordRecoveryBegin,
				onPasswordRecoveryComplete,
				onViewLook,
				onMultiAddToCart,
			}}
		>
			{children}
		</Provider>
	);
};
